import React, { createContext, useContext, useState } from "react";
import { apiGet } from "helpers/Request";
import {
    getMe,
    requestRefreshToken,
    getPublicationUuid,
    setPublicationCode,
} from "helpers/Auth";
import { transform as userTransformer } from "schemas/User";

const authContext = createContext();

export function ProvideAuth({ children }) {
    const auth = useProvideAuth();
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
    return useContext(authContext);
};

function useProvideAuth() {
    const [user, setUser] = useState(null);
    const [publication, setPublication] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const hydrateRoles = async (user, pub) => {
        let hydratedRoles = {};
        if (user.is_snworks === true) {
            console.info("User is SNworks user, manually hydrated as admin");
            const resp = await apiGet(`/v4/acl/Administrator`, pub);
            return { Administrator: resp[0].accesses };
        }

        for (let i = 0; i < user.roles.length; i++) {
            const role = user.roles[i];
            if (role.name === "CEO Users") {
                continue;
            }
            let resp;
            try {
                resp = await apiGet(`/v4/acl/${role.name}`, pub);
            } catch (e) {
                console.debug(role.name + " is not a valid CEO role.");
                continue;
            }
            hydratedRoles[role.name] = resp[0].accesses;
        }
        return hydratedRoles;
    };

    /*
        Verify Auth should only be called once. Currently, called in the ApplicationShell component
        with a sing bound useEffect:

        ```
            useEffect(() => {
                auth.verifyAuth();
            }, []);
        ```
    */
    const verifyAuth = async () => {
        console.info("User verification");
        let me;

        try {
            me = await getMe();
            if (!me || me === 401) {
                console.info("Request token refresh");
                const refresh = await requestRefreshToken();
                if (refresh) {
                    console.info("Restart verification");
                    me = await getMe();
                }
            }

            if (!me || me === 401) {
                console.log("Failed to authenticate");
                return false;
            }
        } catch (e) {
            console.log(e);
            if (e === "server unavailable") {
                return -1;
            }
            return false;
        }

        console.info("User verified");
        setIsAuthenticated(true);
        const user = userTransformer(me);
        setUser(user);

        if (getPublicationUuid()) {
            const pub = user.licenses.find(
                (license) => license.uuid === getPublicationUuid()
            );
            if (pub) {
                console.info("Publication set");
                setPublicationCode(pub.client_code);
                setPublication(pub);

                console.log("Hydrate ACLs");
                user.hydrateRoles(await hydrateRoles(user, pub));
            }
        }

        return true;
    };

    return {
        user,
        publication,
        isAuthenticated,
        verifyAuth,
    };
}
